// Copyright (c) StrangeBee 2023

import React, { ReactElement, useState } from 'react';
import { ObservableOutput, ObservableInput } from 'thehive-sdk';
import { useLocalMessage } from '@Helpers/';
import { ObservableCreate } from '@Organisms/Observable/ObservableCreate/ObservableCreate';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { formatObservableTitle } from '@Helpers/observable';

interface AnalysisObservableCreationProps {
    baseObservable: ObservableOutput;
    observables: ObservableOutput[];
    observableKeys: React.ReactText[];
    setObservableKeys: React.Dispatch<React.SetStateAction<React.ReactText[]>>;
    analyzerName: string;
    onCreate: (values: ObservableInput, isLast: boolean) => Promise<any>;
    onEnd: () => void;
}

const AnalysisObservableCreation = ({
    baseObservable,
    observables,
    observableKeys,
    setObservableKeys,
    analyzerName,
    onCreate,
    onEnd,
}: AnalysisObservableCreationProps): ReactElement => {
    const i = useLocalMessage();

    const [filtered, setFiltered] = useState<ObservableOutput[]>([]);
    const [visible, setVisible] = useState(false);
    const [index, setIndex] = useState(0);

    const handlePool = () => {
        setVisible(true);

        const filteredObservables = observables.reduce((acc, observable) => {
            if (!observableKeys.includes(observable._id)) {
                return acc;
            }

            const prevObservableIndex = acc.findIndex(
                ({ dataType }) => dataType === observable.dataType && dataType !== 'file',
            );

            if (prevObservableIndex !== -1) {
                acc[prevObservableIndex] = {
                    ...acc[prevObservableIndex],
                    tags: [...(acc[prevObservableIndex].tags || []), ...(observable.tags || [])],
                    data: acc[prevObservableIndex].data + '\n' + observable.data,
                };
                return acc;
            }

            return [
                ...acc,
                {
                    ...observable,
                    tags: [`src:${analyzerName}`, ...(observable.tags || [])],
                    message:
                        observable.message ||
                        `### Discovered from:\n- Observable: **[${baseObservable.dataType}] - ${formatObservableTitle({
                            data: baseObservable.data,
                            attachment: baseObservable.attachment,
                        })}**\n- Analyzer: **${analyzerName}**`,
                },
            ];
        }, [] as ObservableOutput[]);

        setFiltered(filteredObservables);
    };

    const onFinishHandler = async (values: ObservableInput): Promise<any> => {
        return onCreate(values, index + 1 >= filtered.length).then(() => {
            setIndex((index) => {
                if (index + 1 >= filtered.length) {
                    onPoolEnded();
                    return 0;
                }
                return index + 1;
            });
        });
    };

    const onPoolEnded = () => {
        setFiltered([]);
        setObservableKeys([]);
        onEnd();
        setVisible(false);
    };

    return (
        <div>
            <THButton type="primary" onClick={handlePool} disabled={!observableKeys.length}>
                {i('analyzer.observable.import')}
            </THButton>

            {!!filtered.length && index < filtered.length && (
                <ObservableCreate
                    key={filtered[index]?._id}
                    defaultValues={{ ...filtered[index], multiline: true }}
                    onFinish={onFinishHandler}
                    defaultVisible
                    visible={visible}
                    onVisibleChange={(visible) => {
                        setVisible(visible);
                        if (!visible) {
                            onPoolEnded();
                        }
                    }}
                />
            )}
        </div>
    );
};

export default AnalysisObservableCreation;
